import { useTranslation } from "react-i18next";
import {
  IconChevronLeft,
  IconFileText,
  IconLoader2,
  IconMinus,
  IconPlaylist,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react";
import {
  ModePanel,
  ModeTrigger,
  modeShortLabel,
  type ModePanelProps,
} from "../../ui/ModePanel";
import type { GptMode } from "../../../domains/gpt/stores/modeStore";
import { InlineButton } from "../../ui/InlineButton";
import { cn } from "../../../lib/utils";
import { iosGenerateHaptic } from "../../../tools/ios";
import { HapticEffect } from "../../../interfaces";
import type { ChatAttachments as ChatAttachmentsState } from "../../../domains/gpt/hooks/useChatAttachments";
import {
  referenceKey,
  type ChatReferenceKind,
  type ChatReferences as ChatReferencesState,
} from "../../../domains/gpt/hooks/useChatReferences";
import {
  useReferenceableArticles,
  useReferenceablePlaylists,
  MIN_ARTICLE_QUERY_LENGTH,
} from "../../../domains/gpt/hooks/useReferenceableSources";
import type { ChatComposerAdd } from "../../../domains/gpt/hooks/useChatComposerAdd";

// The composer's "+" affordance: replaces the bare paperclip with a menu that
// adds EITHER a file (the existing attachment flow) OR a Juisci source to
// converse with — one of the user's playlists, or any article they can read.
//
// It comes in two halves because it lives in two places: the CONTROL sits at the
// head of the composer's button row, the PANEL rises out of the top of the card
// — the same surface the Sources panel opens onto (`ChatInput`'s `leadingPanel`),
// on every viewport. It replaces the popover/bottom-sheet pair this used to be:
// a sheet is a heavy, screen-owning surface for a short menu that belongs
// visually to the composer it was tapped from. The open-state the two halves
// share is `useChatComposerAdd`.
//
// The two source views differ by catalogue size, not by taste: a playlist list is
// short and owned, so it is listed outright; the article corpus is not, so that
// view is a search field.
//
// On desktop the "+" also holds the answer mode (docs/gpt-mode-router.md): the
// Figma composer keeps only "+", dictation and send in its button row, so the
// mode picker that sits in that row on the phone moves into this menu there.

// PDFs go through OCR; everything else is read as text. Announced up-front so the
// picker does not offer what the server will refuse (it re-checks the bytes).
const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/json",
  "application/xml",
  "text/*",
  ".rdf",
  ".bib",
  ".ris",
  ".csv",
  ".json",
  ".xml",
  ".md",
  ".txt",
].join(",");

/**
 * The "+" in the button row, and the file picker it owns. Where the "+" holds
 * the mode (desktop), a mode other than Auto also shows as a chip beside it:
 * hidden in a menu, a forced mode would route every question without the
 * reader seeing it. The chip opens the menu straight on the modes.
 */
export function ChatComposerAddTrigger({
  add,
  attachments,
  references,
  disabled,
  mode,
}: {
  add: ChatComposerAdd;
  attachments: ChatAttachmentsState;
  references: ChatReferencesState;
  // True while nothing can hold what the reader would add.
  disabled: boolean;
  // The selected mode when the "+" menu holds it, or null.
  mode: GptMode | null;
}): React.ReactElement {
  const { t } = useTranslation();
  const isBusy = attachments.isBusy || references.isBusy;
  const isOpen = add.state.view !== "closed";

  return (
    <>
      <InlineButton
        state={disabled ? "disabled" : isBusy ? "pending" : "default"}
        aria-label={t("Add a file, a playlist or an article")}
        aria-expanded={isOpen}
        aria-controls={add.panelId}
        onClick={() => {
          if (isBusy) return;
          iosGenerateHaptic(HapticEffect.SELECTION);
          if (isOpen) {
            add.close();
            return;
          }
          add.open();
        }}
      >
        {isBusy ? (
          <IconLoader2 className="animate-spin" />
        ) : (
          // The glyph says which way the tap goes: a "+" that opens, a "−" that
          // closes. It is ONE stroke drawn twice, not two icons cross-faded —
          // crossed they read as a plus, superposed as a minus, so the swap is a
          // quarter turn of the second stroke with nothing fading in or out. Both
          // are the real tabler glyph, so the weight matches its neighbours, and
          // they share the button's 19px box (`InlineButton` sizes its DIRECT svg
          // child, and here that child is this wrapper).
          <span className="relative inline-flex size-4.75 items-center justify-center">
            <IconMinus className="absolute size-4.75" />
            <IconMinus
              className={cn(
                "absolute size-4.75 transition-transform duration-300 ease-out motion-reduce:transition-none",
                isOpen ? "rotate-0" : "rotate-90"
              )}
            />
          </span>
        )}
      </InlineButton>
      <input
        ref={add.fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        className="hidden"
        onChange={(e) => {
          attachments.onPick(e.target.files);
          // Reset, so re-picking the SAME file fires `change` again.
          e.target.value = "";
        }}
      />
      {mode !== null && mode !== "auto" && (
        <ModeTrigger
          selected={mode}
          open={add.state.view === "modes"}
          controls={add.panelId}
          onClick={() => {
            iosGenerateHaptic(HapticEffect.SELECTION);
            if (add.state.view === "modes") {
              add.close();
              return;
            }
            add.showModes();
          }}
        />
      )}
    </>
  );
}

/**
 * The panel above the composer — what the "+" opens. Four views: the menu, the
 * playlist list, the article search, and the answer modes. Rendered by
 * `ChatInput`, which owns the strip it sits on (and dismisses it on an outside
 * tap or Escape).
 */
export function ChatComposerAddPanel({
  add,
  references,
  mode,
}: {
  add: ChatComposerAdd;
  references: ChatReferencesState;
  // The answer mode, when this menu offers it (the desktop composer, whose
  // button row has no mode picker). Null where the composer shows its own
  // picker, or where the mode is not a choice.
  mode: Omit<ModePanelProps, "className"> | null;
}): React.ReactElement {
  const { t } = useTranslation();

  const onSelect = (
    kind: ChatReferenceKind,
    targetId: string,
    title: string
  ): void => {
    iosGenerateHaptic(HapticEffect.SELECTION);
    void references.onAdd(kind, targetId, title);
    add.close();
  };

  switch (add.state.view) {
    case "closed":
      throw new Error("should not happen: the add panel is rendered closed");
    case "menu":
      return (
        <PanelSurface>
          <ul className="m-0 list-none p-0">
            <MenuRow
              icon={<IconFileText size={22} className="text-ds-mint-800" />}
              label={t("File")}
              sublabel={t("Attach a PDF or document")}
              disabled={false}
              onSelect={add.pickFile}
            />
            <MenuRow
              icon={<IconPlaylist size={22} className="text-ds-mint-800" />}
              label={t("Playlist")}
              sublabel={t("Reference one of your playlists")}
              disabled={false}
              onSelect={add.showPlaylists}
            />
            <MenuRow
              icon={<IconSearch size={22} className="text-ds-mint-800" />}
              label={t("Article")}
              sublabel={t("Search the Juisci library and chat with a paper")}
              disabled={false}
              onSelect={add.showArticles}
            />
            {mode !== null && (
              <MenuRow
                icon={<IconSparkles size={22} className="text-ds-mint-800" />}
                label={t("Mode")}
                sublabel={modeShortLabel(mode.selected, t)}
                disabled={false}
                onSelect={add.showModes}
              />
            )}
          </ul>
        </PanelSurface>
      );
    case "modes":
      if (mode === null)
        throw new Error("should not happen: the mode view without a mode");
      return (
        <PanelSurface>
          <PanelHeader title={t("Mode")} onBack={add.showMenu} />
          <ModePanel
            selected={mode.selected}
            onSelectChange={(next) => {
              iosGenerateHaptic(HapticEffect.SELECTION);
              mode.onSelectChange(next);
              add.close();
            }}
            // The panel surface already draws the card.
            className="border-0 shadow-none"
          />
        </PanelSurface>
      );
    case "playlists":
      return (
        <PanelSurface>
          <PanelHeader
            title={t("Reference a playlist")}
            onBack={add.showMenu}
          />
          <PlaylistRows
            referencedKeys={references.referencedKeys}
            onSelect={(playlistId, title) => {
              onSelect("playlist", playlistId, title);
            }}
          />
        </PanelSurface>
      );
    case "articles": {
      const query = add.state.query;
      return (
        <PanelSurface>
          <PanelHeader
            title={t("Reference an article")}
            onBack={add.showMenu}
          />
          <div className="shrink-0 px-2 pb-2 pt-3">
            <input
              type="search"
              // The user opened this view to type — landing in the field is the
              // whole point, and on iOS it raises the keyboard with the panel.
              autoFocus
              value={query}
              onChange={(e) => {
                add.setArticleQuery(e.target.value);
              }}
              placeholder={t("Search an article by title, topic, author…")}
              aria-label={t("Search an article")}
              className={cn(
                "w-full rounded-xl border border-solid border-ds-slate-200 bg-ds-white",
                "px-3 py-2 font-dm-sans text-sm text-ds-black outline-none",
                "placeholder:text-ds-slate-400 focus:border-ds-mint-800"
              )}
            />
          </div>
          <ArticleRows
            query={query}
            referencedKeys={references.referencedKeys}
            onSelect={(articleId, title) => {
              onSelect("article", articleId, title);
            }}
          />
        </PanelSurface>
      );
    }
    default:
      throw new Error(add.state satisfies never);
  }
}

/** The user's created + saved playlists, deduped. */
function PlaylistRows({
  referencedKeys,
  onSelect,
}: {
  referencedKeys: string[];
  onSelect: (playlistId: string, title: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  // The panel only mounts while open, so the query runs exactly when it shows.
  const { playlists, isLoading } = useReferenceablePlaylists(true);
  const alreadyReferenced = new Set(referencedKeys);

  if (isLoading) return <PanelSpinner />;

  if (playlists.length === 0)
    return <PanelNote>{t("You don't have any playlists yet.")}</PanelNote>;

  return (
    <ul className="m-0 list-none p-0">
      {playlists.map((playlist) => {
        const isReferenced = alreadyReferenced.has(
          referenceKey("playlist", playlist.id)
        );
        return (
          <MenuRow
            key={playlist.id}
            icon={<IconPlaylist size={22} className="text-ds-mint-800" />}
            label={playlist.title}
            sublabel={isReferenced ? t("Already referenced") : null}
            disabled={isReferenced}
            onSelect={() => {
              onSelect(playlist.id, playlist.title);
            }}
          />
        );
      })}
    </ul>
  );
}

/** Articles matching the panel's search — the corpus the reader can access. */
function ArticleRows({
  query,
  referencedKeys,
  onSelect,
}: {
  query: string;
  referencedKeys: string[];
  onSelect: (articleId: string, title: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const { articles, isLoading } = useReferenceableArticles(query);
  const alreadyReferenced = new Set(referencedKeys);

  if (query.trim().length < MIN_ARTICLE_QUERY_LENGTH)
    return (
      <PanelNote>
        {t("Type a few words to search the Juisci library.")}
      </PanelNote>
    );
  if (isLoading) return <PanelSpinner />;
  if (articles.length === 0)
    return <PanelNote>{t("No article matches this search.")}</PanelNote>;

  return (
    <ul className="m-0 list-none p-0">
      {articles.map((article) => {
        const isReferenced = alreadyReferenced.has(
          referenceKey("article", article.id)
        );
        return (
          <MenuRow
            key={article.id}
            icon={<IconFileText size={22} className="text-ds-mint-800" />}
            label={article.title}
            sublabel={isReferenced ? t("Already referenced") : null}
            disabled={isReferenced}
            onSelect={() => {
              onSelect(article.id, article.title);
            }}
          />
        );
      })}
    </ul>
  );
}

/** The back arrow + section label the two drill-down views share. */
function PanelHeader({
  title,
  onBack,
}: {
  title: string;
  onBack: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 items-center gap-1 px-2 pt-2">
      <button
        type="button"
        aria-label={t("Back")}
        onClick={onBack}
        className={cn(
          "flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-xl",
          "border-0 bg-transparent text-ds-slate-500",
          "hover:bg-ds-slate-50 active:bg-ds-slate-100"
        )}
      >
        <IconChevronLeft size={18} />
      </button>
      <span className="font-dm-sans text-xs font-semibold uppercase tracking-[0.55px] text-ds-slate-500">
        {title}
      </span>
    </div>
  );
}

function PanelSpinner(): React.ReactElement {
  return (
    <div className="flex items-center justify-center py-8">
      <IconLoader2 className="size-5 animate-spin text-ds-slate-400" />
    </div>
  );
}

function PanelNote({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <p className="px-4 py-6 text-center font-dm-sans text-sm text-ds-slate-500">
      {children}
    </p>
  );
}

/** The card every view shares — the Sources panel's surface, so the two controls
 *  of one composer open onto the same thing. */
function PanelSurface({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "flex max-h-[50vh] w-full flex-col overflow-hidden rounded-2xl py-1",
        "border border-solid border-ds-slate-200 bg-ds-white",
        "shadow-[0px_4px_12px_rgba(17,24,39,0.08)]"
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

function MenuRow({
  icon,
  label,
  sublabel,
  disabled,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string | null;
  disabled: boolean;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          // `min-h` rather than a fixed height: an article title is allowed to
          // wrap to a second line (see the label's clamp), and a fixed row would
          // clip it.
          "flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left",
          "border-0 bg-transparent",
          disabled
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer hover:bg-ds-slate-50 active:bg-ds-slate-100"
        )}
      >
        <span className="inline-flex size-6 shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className="flex min-w-0 flex-col">
          {/* Two lines, at a smaller size than the one-line version was: an
              article title truncated to half a sentence is unpickable — the
              distinguishing words of a paper live at its end. */}
          <span className="line-clamp-2 font-dm-sans text-sm/snug font-medium  text-ds-black">
            {label}
          </span>
          {sublabel === null ? null : (
            <span className="truncate font-dm-sans text-[12px]/4.5 text-ds-slate-500">
              {sublabel}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}
